import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eraser, Pencil } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { EmptyState } from '@/components/app/EmptyState';
import { GaugeChart } from '@/components/app/GaugeChart';
import { Heatmap24Hour } from '@/components/app/Heatmap24Hour';
import { Heatmap168Hour } from '@/components/app/Heatmap168Hour';
import { CodeBlock } from '@/components/app/CodeBlock';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { humanizeError } from '@/lib/apiClient';
import {
  useClearUserOverride,
  useJourneys,
  useSetUserOverride,
  useUserNotificationProfile,
} from '../hooks';
import { OverrideForm } from '../components/OverrideForm';
import { DecisionsBrowser } from '../components/DecisionsBrowser';

export function UserInspectorPage() {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState(paramUserId ?? '');
  const [overrideOpen, setOverrideOpen] = React.useState(false);
  const confirm = useConfirmDialog();

  const userId = paramUserId && paramUserId.trim() !== '' ? paramUserId : undefined;

  // Keep the search input in sync with the URL param — e.g. when the
  // user clicks back/forward after inspecting another user.
  React.useEffect(() => {
    setSearch(paramUserId ?? '');
  }, [paramUserId]);

  const profile = useUserNotificationProfile(userId);
  const journeys = useJourneys();
  const setOverrideMut = useSetUserOverride();
  const clearOverrideMut = useClearUserOverride();

  const windows = React.useMemo(() => extractWindows(profile.data?.engagementWindows), [profile.data]);

  const handleClear = () => {
    if (!userId) return;
    confirm({
      title: 'Clear all overrides?',
      destructive: true,
      reasonPlaceholder: 'Why are you clearing this override?',
      onConfirm: async ({ reason }) => {
        await clearOverrideMut.mutateAsync({ userId, reason });
      },
    });
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/notifications/dashboard')}>
        <ArrowLeft className="h-4 w-4" /> Health dashboard
      </Button>

      <PageHeader
        title="User inspector"
        description="Inspect engagement profile, recent decisions, and set per-user notification overrides."
      />

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (search.trim()) navigate(`/notifications/users/${encodeURIComponent(search.trim())}`);
            }}
            className="flex w-full max-w-xl items-center gap-2"
          >
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Paste a userId (document id)"
              aria-label="User id"
              className="font-mono"
            />
            <Button type="submit" disabled={!search.trim()}>Inspect</Button>
          </form>
        </CardContent>
      </Card>

      {!userId && (
        <EmptyState
          title="Pick a user to inspect"
          description="Users' engagement, overrides, and recent decisions surface once you paste a userId above."
        />
      )}

      {userId && profile.isLoading && <LoadingState label={`Loading ${userId}`} />}
      {userId && profile.isError && (
        <ErrorState
          title="User profile unavailable"
          description={humanizeError(profile.error)}
          actionLabel="Retry"
          onAction={() => profile.refetch()}
        />
      )}

      {userId && profile.data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="font-mono text-base">{profile.data.userId}</span>
                {profile.data.email && <span className="text-sm text-muted-foreground">· {profile.data.email}</span>}
                {profile.data.timeZone && <Badge variant="muted">{profile.data.timeZone}</Badge>}
              </CardTitle>
              <CardDescription>
                Engagement score powers personalized timing and cap weighting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-6">
                <GaugeChart value={profile.data.engagementScore} label="Engagement" />
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Hourly histogram (24h)
                    </div>
                    <Heatmap24Hour hours={windows.hour24} activeHours={windows.activeHours} />
                  </div>
                  {windows.dow168 && (
                    <div>
                      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Day-of-week histogram (168h)
                      </div>
                      <Heatmap168Hour cells={windows.dow168} />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Notification overrides</CardTitle>
                <CardDescription>
                  Mutes and forced windows. Every change writes an audit entry with reason.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setOverrideOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={!profile.data.notificationOverrides}
                  onClick={handleClear}
                >
                  <Eraser className="h-3.5 w-3.5" /> Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {profile.data.notificationOverrides ? (
                <CodeBlock value={profile.data.notificationOverrides} />
              ) : (
                <EmptyState title="No overrides set" description="This user inherits default notification behavior." />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent decisions</CardTitle>
              <CardDescription>
                Every notification the Decision Brain evaluated for this user. Filter by outcome or
                pull just the ones the user opened. Click a row for the full content + engagement
                trace.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <DecisionsBrowser userId={profile.data.userId} pageSize={20} />
            </CardContent>
          </Card>

          <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit overrides for {profile.data.userId}</DialogTitle>
                <DialogDescription>
                  Every field is optional. Empty values leave the existing override untouched.
                </DialogDescription>
              </DialogHeader>
              <OverrideForm
                initial={profile.data.notificationOverrides ?? null}
                journeys={journeys.data?.journeys ?? []}
                submitting={setOverrideMut.isPending}
                onCancel={() => setOverrideOpen(false)}
                onSubmit={async ({ reason, overrides }) => {
                  await setOverrideMut.mutateAsync({ userId, reason, overrides });
                  setOverrideOpen(false);
                }}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

interface ExtractedWindows {
  hour24: number[] | undefined;
  dow168: number[] | undefined;
  activeHours: number[] | undefined;
}

function extractWindows(raw: unknown): ExtractedWindows {
  if (!raw || typeof raw !== 'object') return { hour24: undefined, dow168: undefined, activeHours: undefined };
  const r = raw as Record<string, unknown>;

  // 24-element hour histogram. EngagementWindowComputer writes `hourScores`
  // (the canonical name); older clients/tests used `hour24` or `histogram`.
  const hour24 =
    Array.isArray(r.hourScores) && (r.hourScores as unknown[]).length === 24
      ? (r.hourScores as number[])
      : Array.isArray(r.hour24) && (r.hour24 as unknown[]).length === 24
        ? (r.hour24 as number[])
        : Array.isArray(r.histogram) && (r.histogram as unknown[]).length === 24
          ? (r.histogram as number[])
          : undefined;

  // Active-hour windows arrive as either a flat number[] (legacy) or as
  // an array of `{start, end}` ranges. Expand ranges into a flat hour list
  // so the heatmap component can highlight them uniformly.
  let activeHours: number[] | undefined;
  if (Array.isArray(r.windows)) {
    const arr = r.windows as unknown[];
    if (arr.every((x) => typeof x === 'number')) {
      activeHours = arr as number[];
    } else if (arr.every((x) => x !== null && typeof x === 'object' && 'start' in (x as object) && 'end' in (x as object))) {
      const flat: number[] = [];
      for (const w of arr as Array<{ start: number; end: number }>) {
        if (typeof w.start !== 'number' || typeof w.end !== 'number') continue;
        // Skip malformed ranges so out-of-range hours never reach the heatmap.
        // Valid hours are 0..23 inclusive on both ends.
        if (!Number.isInteger(w.start) || !Number.isInteger(w.end)) continue;
        if (w.start < 0 || w.start > 23 || w.end < 0 || w.end > 23) continue;
        // Ranges are inclusive on both ends in EngagementWindowComputer output.
        // Wrap-around (e.g. 22→2) is rare but possible; expand both directions.
        if (w.end >= w.start) {
          for (let h = w.start; h <= w.end; h += 1) flat.push(h);
        } else {
          for (let h = w.start; h <= 23; h += 1) flat.push(h);
          for (let h = 0; h <= w.end; h += 1) flat.push(h);
        }
      }
      activeHours = Array.from(new Set(flat));
    }
  }

  return {
    hour24,
    dow168: Array.isArray(r.dow168) && (r.dow168 as unknown[]).length === 168
      ? (r.dow168 as number[])
      : undefined,
    activeHours,
  };
}
